import React, { useState, useEffect } from 'react';
import Layout from './components/Layout';
import KPICard from './components/KPICard';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { apiGet } from './utils/api';

// Modern gradient-inspired color palette (for timeline and currency)
const COLORS = [
  '#667eea', // Purple-blue
  '#10b981', // Emerald green
  '#f59e0b', // Amber
  '#ec4899', // Pink
  '#3b82f6', // Sky blue
  '#8b5cf6', // Violet
  '#14b8a6', // Teal
  '#f97316', // Orange
  '#06b6d4', // Cyan
  '#a855f7', // Purple
];

// Monochromatic shades for asset allocation (subtle greys)
const GREY_SHADES = [
  '#1e293b', // Darkest
  '#334155',
  '#475569',
  '#64748b',
  '#94a3b8',
  '#cbd5e1',
  '#e2e8f0',
  '#f1f5f9', // Lightest
  '#cbd5e1',
  '#94a3b8',
];

function Dashboard() {
  const [kpiData, setKpiData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timelineData, setTimelineData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(['Gesamt']);
  const [showCurrencyChart, setShowCurrencyChart] = useState(false);
  const [privacyMode, setPrivacyMode] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedGrowthCategory, setSelectedGrowthCategory] = useState('Gesamt');

  useEffect(() => {
    fetchKPIData();
    fetchTimelineData();
  }, []);

  const fetchKPIData = () => {
    setIsLoading(true);
    apiGet('/kpi/summary')
      .then(res => res.json())
      .then(data => {
        setKpiData(data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching KPI data:', err);
        setIsLoading(false);
      });
  };

  const fetchTimelineData = () => {
    apiGet('/wealth_summary')
      .then(res => res.json())
      .then(data => {
        setTimelineData(data);
        // Extract all unique category names from all data points
        if (data.length > 0) {
          const allCategories = new Set();
          data.forEach(monthData => {
            Object.keys(monthData).forEach(key => {
              if (key !== 'month') {
                allCategories.add(key);
              }
            });
          });
          const categoryNames = Array.from(allCategories);
          setCategories(categoryNames);
        }
      })
      .catch(err => console.error('Error fetching timeline data:', err));
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        // Don't allow deselecting all categories
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const formatCurrency = (value) => {
    const formatted = new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
    return privacyMode ? '***' : formatted;
  };

  const formatNumber = (value) => {
    const formatted = new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
    return privacyMode ? '***' : formatted;
  };

  const calculateGrowth = (category) => {
    if (timelineData.length === 0) return { yoy: null, ytd: null, currentValue: 0 };

    const latestMonth = timelineData[timelineData.length - 1];
    const currentValue = latestMonth[category] || 0;

    // YOY: Compare to 12 months ago
    const twelveMonthsAgo = timelineData.length >= 12 ? timelineData[timelineData.length - 12] : null;
    const yoyValue = twelveMonthsAgo ? twelveMonthsAgo[category] || 0 : 0;
    const yoy = yoyValue > 0 ? (((currentValue - yoyValue) / yoyValue) * 100).toFixed(1) : null;

    // YTD: Compare to January of this year
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const januaryData = timelineData.find(d => {
      const month = d.month || '';
      return month.startsWith(currentYear + '-01') || month.startsWith('01.' + currentYear);
    });
    const januaryValue = januaryData ? januaryData[category] || 0 : 0;
    const ytd = januaryValue > 0 ? (((currentValue - januaryValue) / januaryValue) * 100).toFixed(1) : null;

    return { yoy, ytd, currentValue };
  };

  return (
    <Layout title="Dashboard">
      <div className="space-y-6">
        {/* Privacy Toggle */}
        <div className="flex justify-end">
          <button
            onClick={() => setPrivacyMode(!privacyMode)}
            className="btn btn-sm btn-secondary flex items-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {privacyMode ? (
                // Eye-off icon (privacy ON)
                <>
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </>
              ) : (
                // Eye icon (privacy OFF)
                <>
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </>
              )}
            </svg>
            {privacyMode ? 'Zahlen anzeigen' : 'Privatsphäre'}
          </button>
        </div>

        {/* KPI Cards Grid - 1 col mobile, 2 col tablet, 4 col desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <KPICard
            title="Gesamtvermögen"
            value={kpiData ? formatCurrency(kpiData.net_worth.value) : '-'}
            subtitle="Total Net Worth"
            isLoading={isLoading}
          />
          <KPICard
            title="Flüssige Mittel"
            value={kpiData ? formatCurrency(kpiData.liquid_assets.value) : '-'}
            subtitle="Liquid Assets"
            isLoading={isLoading}
          />
          <KPICard
            title="Wachstum (MoM)"
            value={kpiData ? `${kpiData.growth_rate.mom}%` : '-'}
            subtitle="Month-over-Month"
            trend={kpiData ? kpiData.growth_rate.mom : null}
            isLoading={isLoading}
          />
          <KPICard
            title="Wachstum (YoY)"
            value={kpiData ? `${kpiData.growth_rate.yoy}%` : '-'}
            subtitle="Year-over-Year"
            trend={kpiData ? kpiData.growth_rate.yoy : null}
            isLoading={isLoading}
          />
        </div>

        {/* Timeline Chart */}
        {timelineData.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg md:text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                Vermögensentwicklung im Zeitverlauf
              </h3>
            </div>

            <div className="card-body space-y-4">
              {/* Category Toggle Buttons - Kompakter */}
              <div className="flex flex-wrap gap-1.5">
                {categories.map((category, index) => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className={`${
                      selectedCategories.includes(category)
                        ? 'bg-primary-600 text-white'
                        : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300'
                    } px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:shadow-md`}
                    style={{
                      borderLeft: selectedCategories.includes(category)
                        ? `3px solid ${COLORS[index % COLORS.length]}`
                        : '3px solid transparent'
                    }}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Timeline Area Chart */}
              {timelineData.length > 1 ? (
                <div className="w-full overflow-x-auto">
                  <ResponsiveContainer
                    width="100%"
                    height={window.innerWidth < 640 ? 500 : window.innerWidth < 1024 ? 550 : 600}
                  >
                    <AreaChart
                      data={timelineData}
                      margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                    >
                      <defs>
                        {selectedCategories.map((category) => {
                          const colorIndex = categories.indexOf(category);
                          const color = COLORS[colorIndex % COLORS.length];
                          return (
                            <linearGradient key={`gradient-${category}`} id={`color-${category}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={color} stopOpacity={0}/>
                            </linearGradient>
                          );
                        })}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="month"
                        stroke="#64748b"
                        style={{ fontSize: '12px' }}
                        interval={window.innerWidth < 768 ? Math.floor(timelineData.length / 6) : 'preserveStartEnd'}
                        angle={window.innerWidth < 768 ? -45 : 0}
                        textAnchor={window.innerWidth < 768 ? 'end' : 'middle'}
                        height={window.innerWidth < 768 ? 60 : 30}
                      />
                      <YAxis
                        tickFormatter={(value) => {
                          const sign = value < 0 ? '-' : '';
                          const abs = Math.abs(value);
                          if (abs >= 1000000) {
                            return `${sign}${(abs / 1000000).toFixed(1)}M`;
                          } else if (abs >= 1000) {
                            return `${sign}${(abs / 1000).toFixed(0)}K`;
                          }
                          return value.toString();
                        }}
                        tickCount={6}
                        stroke="#64748b"
                        style={{ fontSize: '12px' }}
                        width={window.innerWidth < 768 ? 50 : 60}
                      />
                      <Tooltip
                        formatter={(v) => `${v.toLocaleString()} CHF`}
                        labelFormatter={(m) => `Monat: ${m}`}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.5rem',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        }}
                      />
                      <Legend
                        wrapperStyle={{
                          paddingTop: '20px',
                          fontSize: '14px'
                        }}
                      />
                      {selectedCategories.map((category) => {
                        const colorIndex = categories.indexOf(category);
                        const color = COLORS[colorIndex % COLORS.length];
                        return (
                          <Area
                            key={category}
                            type="monotone"
                            dataKey={category}
                            stroke={color}
                            strokeWidth={category === 'Gesamt' ? 3 : 2}
                            fill={`url(#color-${category})`}
                            activeDot={{ r: 6 }}
                          />
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-8 text-secondary-500">
                  Noch keine Verlaufsdaten vorhanden. Mindestens 2 Monate benötigt.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Charts Row */}
        {!isLoading && kpiData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Asset Allocation Chart */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg md:text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                  Vermögensallokation nach Kategorie
                </h2>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart onMouseLeave={() => setActiveIndex(null)}>
                    <Pie
                      data={kpiData.asset_allocation}
                      dataKey="value"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      onMouseEnter={(_, index) => setActiveIndex(index)}
                    >
                      {kpiData.asset_allocation.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={activeIndex === index ? COLORS[index % COLORS.length] : GREY_SHADES[index % GREY_SHADES.length]}
                          style={{
                            transition: 'fill 0.3s ease',
                            cursor: 'pointer'
                          }}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Currency Exposure Chart - Only show if enabled */}
            {showCurrencyChart && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg md:text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                    Währungsexposure
                  </h2>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={kpiData.currency_exposure}
                        dataKey="value"
                        nameKey="currency"
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        label={({currency, percentage}) => `${currency}: ${percentage}%`}
                        labelLine={true}
                      >
                        {kpiData.currency_exposure.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Currency Toggle */}
        {!isLoading && kpiData && kpiData.currency_exposure.length > 1 && (
          <div className="flex justify-end">
            <button
              onClick={() => setShowCurrencyChart(!showCurrencyChart)}
              className="btn btn-sm btn-secondary text-xs"
            >
              {showCurrencyChart ? 'Währungsexposure ausblenden' : 'Währungsexposure anzeigen'}
            </button>
          </div>
        )}

        {/* Asset Allocation Table */}
        {!isLoading && kpiData && (
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg md:text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                Detaillierte Vermögensallokation
              </h3>
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                <thead className="bg-secondary-50 dark:bg-secondary-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Kategorie
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Wert (CHF)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Anteil (%)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
                  {kpiData.asset_allocation.map((item, index) => (
                    <tr
                      key={index}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(null)}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span
                            className="inline-block w-3 h-3 rounded-sm mr-2 transition-colors"
                            style={{
                              backgroundColor: activeIndex === index ? COLORS[index % COLORS.length] : GREY_SHADES[index % GREY_SHADES.length]
                            }}
                          ></span>
                          <span className="text-sm text-secondary-900 dark:text-secondary-100">{item.category}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-secondary-900 dark:text-secondary-100">
                        {formatNumber(item.value)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-secondary-900 dark:text-secondary-100">
                        {item.percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden card-body space-y-3">
              {kpiData.asset_allocation.map((item, index) => (
                <div
                  key={index}
                  className="bg-secondary-50 dark:bg-secondary-700 rounded-lg p-4 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-sm"
                        style={{
                          backgroundColor: GREY_SHADES[index % GREY_SHADES.length]
                        }}
                      ></span>
                      <span className="font-medium text-secondary-900 dark:text-secondary-100">{item.category}</span>
                    </div>
                    <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                      {item.percentage}%
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                      {formatNumber(item.value)} CHF
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Currency Exposure Table - Only show if enabled */}
        {!isLoading && kpiData && showCurrencyChart && (
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg md:text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                Währungsexposure Details
              </h3>
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                <thead className="bg-secondary-50 dark:bg-secondary-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Währung
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Wert in CHF
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Anteil (%)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
                  {kpiData.currency_exposure.map((item, index) => (
                    <tr key={index} className="hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span
                            className="inline-block w-3 h-3 rounded-sm mr-2"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></span>
                          <span className="text-sm text-secondary-900 dark:text-secondary-100">{item.currency}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-secondary-900 dark:text-secondary-100">
                        {formatNumber(item.value)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-secondary-900 dark:text-secondary-100">
                        {item.percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden card-body space-y-3">
              {kpiData.currency_exposure.map((item, index) => (
                <div
                  key={index}
                  className="bg-secondary-50 dark:bg-secondary-700 rounded-lg p-4 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-sm"
                        style={{
                          backgroundColor: COLORS[index % COLORS.length]
                        }}
                      ></span>
                      <span className="font-medium text-secondary-900 dark:text-secondary-100">{item.currency}</span>
                    </div>
                    <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                      {item.percentage}%
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                      {formatNumber(item.value)} CHF
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* YOY and YTD Growth Metrics */}
        {timelineData.length > 0 && categories.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg md:text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                Wachstumsanalyse
              </h3>
            </div>

            <div className="card-body space-y-6">
              {/* Category Selection */}
              <div>
                <label className="label">
                  Kategorie auswählen:
                </label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category, index) => (
                    <button
                      key={category}
                      onClick={() => setSelectedGrowthCategory(category)}
                      className={`${
                        selectedGrowthCategory === category
                          ? 'btn-primary'
                          : 'btn-secondary'
                      } btn btn-sm`}
                      style={{
                        borderLeft: selectedGrowthCategory === category
                          ? `3px solid ${COLORS[index % COLORS.length]}`
                          : '3px solid transparent'
                      }}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Growth KPI Cards */}
              {(() => {
                const growth = calculateGrowth(selectedGrowthCategory);
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    <KPICard
                      title={`${selectedGrowthCategory} - Aktuell`}
                      value={formatCurrency(growth.currentValue)}
                      subtitle="Neuester Stand"
                      isLoading={false}
                    />
                    <KPICard
                      title="YTD Wachstum"
                      value={growth.ytd !== null ? `${growth.ytd}%` : 'N/A'}
                      subtitle="Year-to-Date"
                      trend={growth.ytd !== null ? parseFloat(growth.ytd) : null}
                      isLoading={false}
                    />
                    <KPICard
                      title="YOY Wachstum"
                      value={growth.yoy !== null ? `${growth.yoy}%` : 'N/A'}
                      subtitle="Year-over-Year"
                      trend={growth.yoy !== null ? parseFloat(growth.yoy) : null}
                      isLoading={false}
                    />
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Dashboard;
